import { categorySegment } from "~/lib/utils/url";

import type { NavigationCategory, NavigationSection } from "./types";

/**
 * Filters out subcategories with c_ownerClass === 'LMN' from a category.
 */
export function filterOutLMNCategories(
  category: NavigationCategory,
): NavigationCategory {
  if (!category.categories?.length) return category;
  return {
    ...category,
    categories: category.categories.filter(cat => cat.c_ownerClass !== "LMN"),
  };
}

/**
 * Recursively annotates each category in a tree with `path` — its full slug
 * from the catalog root, joined with `/`. SFCC URL Rules emit multi-segment
 * URLs like `/personal-care/acne-and-skincare`; relying on `id` alone gives
 * leaf-only URLs like `/acne-and-skincare` that don't resolve through
 * `getUrlMapping`. See `app/lib/utils/url.ts:categoryUrlBuilder`.
 *
 * Each segment comes from `categorySegment` (shared with the resolution-side
 * index): the `c_alternativeUrl` first-segment when set, else the slugified
 * display name — matching SFCC's URL Rules — with the catalog id only as a
 * last resort. So slug-aliased parents contribute the right segment
 * (`pain-relief-and-recovery`, not `joint-and-muscle-pain-relief`) and
 * display-name categories emit their real URL (`pain`, not `chronic-pain`).
 * Leaf-direct rendering of an aliased category itself stays handled by
 * `category-link.tsx`'s `altCategoryUrl ||` short-circuit, which uses the full
 * alternativeUrl unchanged.
 */
function injectCategoryPath(
  category: NavigationCategory,
  parentPath: string = "",
): NavigationCategory {
  const ownSlug = categorySegment(category);
  const path = parentPath ? `${parentPath}/${ownSlug}` : ownSlug;
  return {
    ...category,
    path,
    categories: category.categories?.map(child =>
      injectCategoryPath(child, path),
    ),
  };
}

/**
 * Rewrites c_alternativeUrl to match the current hostname when it points to a
 * different HEC site. Relative paths are left untouched.
 */
function rewriteAlternativeUrl(
  category: NavigationCategory,
  hostname: string,
): NavigationCategory {
  if (!category.c_alternativeUrl) return category;

  try {
    const altUrl = new URL(category.c_alternativeUrl);
    if (altUrl.hostname !== hostname) {
      const lastSegment = altUrl.pathname
        .split("/")
        .filter(s => s !== "")
        .pop();
      return {
        ...category,
        c_alternativeUrl: `https://${hostname}/${lastSegment}`,
      };
    }
  } catch {
    // c_alternativeUrl is a relative path — no rewrite needed
  }

  return category;
}

/**
 * Processes raw SFCC categories into grouped navigation sections.
 *
 * - Filters out LMN categories when the current TPA affiliate is not allowed
 * - Filters out hidden categories (no c_showInMenu and no c_showAsMenuOption)
 * - Rewrites cross-site c_alternativeUrl to the current hostname
 * - Groups by c_navigationSection (defaults to "Shop")
 * - Sorts: non-"Shop" sections alphabetically first, "Shop" last
 * - Preserves SFCC sort order within each section
 */
export function buildNavigationSections(
  categories: NavigationCategory[],
  hostname: string,
  lmnAllowed: boolean = true,
  lmnEnable: boolean = true,
): NavigationSection[] {
  // LMN nav links render only when the `lmnEnable` master switch is on (TPA-12)
  // AND the TPA allow-list permits (`lmnAllowed`, TPA-04/17). Master off → LMN
  // nav hidden for everyone, regardless of TPA state.
  const lmnVisible = lmnEnable && lmnAllowed;
  const sections = new Map<string, Map<number, NavigationCategory>>();

  categories.forEach((category, index) => {
    const isLMNCategory = category.c_ownerClass === "LMN";
    if (isLMNCategory && !lmnVisible) return;

    const isHiddenFromMenu =
      !category.c_showInMenu && !category.c_showAsMenuOption;
    if (isHiddenFromMenu) return;

    let processed = rewriteAlternativeUrl(category, hostname);

    // Filter out LMN subcategories when LMN nav isn't visible
    if (!lmnVisible) {
      processed = filterOutLMNCategories(processed);
    }

    // Inject `path` last so the leaf has the final children/parents shape.
    processed = injectCategoryPath(processed);

    const section =
      typeof processed.c_navigationSection === "string" &&
      processed.c_navigationSection
        ? processed.c_navigationSection
        : "Shop";

    if (!sections.has(section)) {
      sections.set(section, new Map());
    }
    sections.get(section)!.set(index, processed);
  });

  return [...sections.entries()]
    .sort(([a], [b]) => {
      if (a === "Shop") return 1;
      if (b === "Shop") return -1;
      return a.localeCompare(b);
    })
    .map(([sectionTitle, categoriesMap]) => ({
      sectionTitle,
      categories: [...categoriesMap.values()],
    }));
}
